/**
 * 防重复请求Hook
 *
 * 功能特性：
 * 1. 防止相同请求的重复发送
 * 2. 支持请求取消
 * 3. 支持自定义请求键生成
 * 4. 支持请求超时处理
 * 5. 提供请求状态管理
 */

import { useRef, useCallback, useEffect } from 'react';

// 待处理请求项接口
interface PendingRequest {
  promise: Promise<any>;
  controller: AbortController;
  timestamp: number;
  resolve: (value: any) => void;
  reject: (reason: any) => void;
}

// Hook配置接口
interface UseRequestDeduplicationConfig {
  timeout?: number; // 请求超时时间（毫秒）
  keyGenerator?: (url: string, params?: any) => string; // 自定义键生成器
  enableLogging?: boolean; // 是否启用日志
}

// Hook返回值接口
interface UseRequestDeduplicationReturn {
  executeRequest: <T>(
    requestFn: (signal?: AbortSignal) => Promise<T>,
    key: string,
    params?: any
  ) => Promise<T>;
  cancelRequest: (url: string, params?: any) => void;
  cancelAllRequests: () => void;
  getPendingRequestsCount: () => number;
  isPending: (key: string) => boolean;
}

/**
 * 防重复请求Hook
 */
export const useRequestDeduplication = (
  config: UseRequestDeduplicationConfig = {}
): UseRequestDeduplicationReturn => {
  const {
    timeout = 30000, // 默认30秒超时
    keyGenerator = (url: string, params?: any) => `${url}:${JSON.stringify(params || {})}`,
    enableLogging = process.env.NODE_ENV === 'development',
  } = config;

  // 存储待处理的请求
  const pendingRequests = useRef<Map<string, PendingRequest>>(new Map());

  // 生成请求键
  const generateKey = useCallback(
    (url: string, params?: any): string => {
      return keyGenerator(url, params);
    },
    [keyGenerator]
  );

  // 清理过期请求
  const cleanupExpiredRequests = useCallback(() => {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [key, request] of pendingRequests.current.entries()) {
      if (now - request.timestamp > timeout) {
        expiredKeys.push(key);
        request.controller.abort();
        request.reject(new Error('Request timeout'));
      }
    }

    expiredKeys.forEach(key => {
      pendingRequests.current.delete(key);
    });

    if (enableLogging && expiredKeys.length > 0) {
      console.log(`[请求去重] 清理了 ${expiredKeys.length} 个超时请求`);
    }
  }, [timeout, enableLogging]);

  // 执行请求
  const executeRequest = useCallback(
    <T>(requestFn: (signal?: AbortSignal) => Promise<T>, url: string, params?: any): Promise<T> => {
      const key = generateKey(url, params);

      // 检查是否已有相同请求在进行中
      const existingRequest = pendingRequests.current.get(key);
      if (existingRequest) {
        return existingRequest.promise;
      }

      // 创建新的AbortController
      const controller = new AbortController();

      // 创建新的Promise
      const promise = new Promise<T>((resolve, reject) => {
        const pendingRequest: PendingRequest = {
          promise: Promise.resolve(), // 临时值，下面会更新
          controller,
          timestamp: Date.now(),
          resolve,
          reject,
        };

        // 存储待处理请求
        pendingRequests.current.set(key, pendingRequest);

        // 执行实际请求
        requestFn(controller.signal)
          .then(result => {
            // 请求成功，清理并返回结果
            pendingRequests.current.delete(key);
            resolve(result);

            if (enableLogging) {
              console.log(`[请求去重] 请求完成: ${key}`);
            }
          })
          .catch(error => {
            // 请求失败，清理并抛出错误
            pendingRequests.current.delete(key);

            // 如果是主动取消，不记录错误
            if (error.name !== 'AbortError') {
              if (enableLogging) {
                console.error(`[请求去重] 请求失败: ${key}`, error);
              }
            }

            reject(error);
          });
      });

      // 更新promise引用
      const pendingRequest = pendingRequests.current.get(key);
      if (pendingRequest) {
        pendingRequest.promise = promise;
      }

      return promise;
    },
    [generateKey, enableLogging]
  );

  // 取消特定请求
  const cancelRequest = useCallback(
    (url: string, params?: any) => {
      const key = typeof url === 'string' && params !== undefined ? generateKey(url, params) : url; // 如果直接传入key

      const request = pendingRequests.current.get(key);
      if (request) {
        request.controller.abort();
        request.reject(new Error('Request cancelled'));
        pendingRequests.current.delete(key);

        if (enableLogging) {
          console.log(`[请求去重] 取消请求: ${key}`);
        }
      }
    },
    [generateKey, enableLogging]
  );

  // 取消所有请求
  const cancelAllRequests = useCallback(() => {
    const count = pendingRequests.current.size;

    for (const [, request] of pendingRequests.current.entries()) {
      request.controller.abort();
      request.reject(new Error('All requests cancelled'));
    }

    pendingRequests.current.clear();

    if (enableLogging && count > 0) {
      console.log(`[请求去重] 取消了所有请求，共 ${count} 个`);
    }
  }, [enableLogging]);

  // 获取待处理请求数量
  const getPendingRequestsCount = useCallback(() => {
    return pendingRequests.current.size;
  }, []);

  // 检查特定请求是否在进行中
  const isPending = useCallback(
    (url: string, params?: any) => {
      const key = typeof url === 'string' && params !== undefined ? generateKey(url, params) : url; // 如果直接传入key

      return pendingRequests.current.has(key);
    },
    [generateKey]
  );

  // 定期清理过期请求
  useEffect(() => {
    const interval = setInterval(cleanupExpiredRequests, 5000); // 每5秒清理一次

    return () => {
      clearInterval(interval);
      // 组件卸载时取消所有请求
      cancelAllRequests();
    };
  }, [cleanupExpiredRequests, cancelAllRequests]);

  return {
    executeRequest,
    cancelRequest,
    cancelAllRequests,
    getPendingRequestsCount,
    isPending,
  };
};

/**
 * 创建请求去重装饰器
 */
export const createRequestDeduplicator = (config?: UseRequestDeduplicationConfig) => {
  const pendingRequests = new Map<string, PendingRequest>();
  const { keyGenerator = (url: string, params?: any) => `${url}:${JSON.stringify(params || {})}` } =
    config || {};

  return {
    execute: <T>(
      requestFn: (signal?: AbortSignal) => Promise<T>,
      url: string,
      params?: any
    ): Promise<T> => {
      const key = keyGenerator(url, params);

      // 检查是否已有相同请求
      const existingRequest = pendingRequests.get(key);
      if (existingRequest) {
        return existingRequest.promise;
      }

      // 创建新请求
      const controller = new AbortController();

      const promise = requestFn(controller.signal).finally(() => {
        pendingRequests.delete(key);
      });

      pendingRequests.set(key, {
        promise,
        controller,
        timestamp: Date.now(),
        resolve: () => {},
        reject: () => {},
      });

      return promise;
    },

    cancel: (url: string, params?: any) => {
      const key = keyGenerator(url, params);
      const request = pendingRequests.get(key);
      if (request) {
        request.controller.abort();
        pendingRequests.delete(key);
      }
    },

    clear: () => {
      for (const request of pendingRequests.values()) {
        request.controller.abort();
      }
      pendingRequests.clear();
    },
  };
};

export default useRequestDeduplication;
